import { useEffect, useRef } from 'react'
import { useEntitlements } from '@/hooks/useEntitlements'
import { useConsent } from '@/lib/consent/useConsent'
import { getActiveProvider } from '@/lib/ads/registry'
import { resolveNpa } from '@/lib/ads/resolveNpa'
import { useAdsPersonalization } from '@/hooks/queries/useAdsPersonalization'

interface BannerAdSlotProps {
  className?: string
}

export default function BannerAdSlot({ className }: BannerAdSlotProps) {
  const { showAds } = useEntitlements()
  const { consent, inConsentRegion, hasDecided } = useConsent()
  const { optedIn } = useAdsPersonalization()
  const ref = useRef<HTMLDivElement>(null)
  // Guard: provider.load() appends an <ins> element into the container — calling
  // it more than once per mount would double-inject the slot. Track here so the
  // effect can include consent/inConsentRegion in deps (plan requirement: value
  // must recompute on consent change) without triggering a double load.
  const hasLoaded = useRef(false)

  useEffect(() => {
    if (!showAds) return
    // In the consent region: wait until the user has made a banner decision.
    // Even non-personalized ads require the user to have seen the banner first.
    if (inConsentRegion && !hasDecided) return
    if (hasLoaded.current) return
    const provider = getActiveProvider('banner')
    const npa = resolveNpa({
      inConsentRegion,
      consentAds: consent?.ads ?? null,
      personalizedAdsOptIn: optedIn,
    })
    console.log('[BannerAdSlot] showAds=true, provider=', provider?.id, '— calling load(), npa=', npa)
    provider?.load(ref.current, { npa })
    hasLoaded.current = true
  }, [showAds, consent, inConsentRegion, hasDecided, optedIn])

  if (!showAds) return null
  // Hold the slot off-screen until consent is decided (consent region only).
  if (inConsentRegion && !hasDecided) return null

  const isDev = import.meta.env.DEV
  return (
    <div
      ref={ref}
      className={className}
      style={{ minHeight: 280, position: 'relative' }}
    >
      {isDev && (
        <span
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontSize: 10,
            color: 'rgba(120,100,80,0.4)',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            pointerEvents: 'none',
          }}
        >
          Ad
        </span>
      )}
    </div>
  )
}
